<script setup lang="ts">
const props = defineProps({
	label: {
		type: String,
		default: undefined,
	},
	name: {
		type: String,
		default: '',
	},
	value: {
		type: [String, Number],
		default: '',
		required: true,
	},
	id: {
		type: String,
		default: '',
		required: true,
	},
	modelValue: {
		type: String,
		default: '',
		required: true,
	},
	disabled: {
		type: Boolean,
		default: false,
	},
});

const emit = defineEmits<{ (e: 'update:modelValue', value: string | number | null): void; }>();
const inputElement = ref<HTMLElement | null>(null);
const slots = useSlots();

function handleInput() {
	emit('update:modelValue', props.value);
}

function handleClick() {
	inputElement.value?.click();
}

const isChecked = computed(() => {
	return props.modelValue === props.value;
});

const hasHelperSlot = computed(() => {
	return !!slots.helperText;
});
</script>

<template>
  <label
    :for="id"
    :class="['radio-container', {'disabled': disabled}]"
  >
    <input
      :id="id"
      ref="inputElement"
      :checked="isChecked"
      :value="value"
      :name="name"
      :disabled="disabled"
      class="radio"
      type="radio"
      @input="handleInput"
    >
    <span
      :class="['checkmark', {'top': hasHelperSlot}]"
      @click="handleClick"
    />
    <span :class="['label', 'text-sm', {'text-medium': hasHelperSlot} ]">{{ label }}</span>
    <p
      v-if="hasHelperSlot"
      class="helper-text"
    >
      <slot name="helperText" />
    </p>
  </label>
</template>

<style lang="scss" scoped>
.radio-container {
	position: relative;
	display: inline-flex;
	flex-direction: column;
	padding-left: rem(24);
	margin: 0;
	@include fonts-get(regular, text-sm);
	color: colors-get(gray, 600);
	cursor: pointer;

	.label {
		margin-bottom: 0;

		&.text-medium {
			color: colors-get(gray, 700);
		}
	}

	&.disabled, &.disabled .checkmark {
		cursor: default;
	}

	&:hover>.checkmark {
		background: colors-get(primary, 100);
		border: solid 1px colors-get(primary, 600);
	}

	.radio {
		position: absolute;
		opacity: 0;
		height: 0;
		width: 0;

		&:checked~.checkmark {
			background: colors-get(primary, 100);
			border: solid 1px colors-get(primary, 600);
		}

		&:checked~.checkmark:after {
			display: block;
		}

		&:focus~.checkmark {
			box-shadow: 0px 0px 0px 4px #F4EBFF;
		}

		&:disabled {
			&~.checkmark {
				background: colors-get(gray, 100);
				border: solid 1px colors-get(gray, 300);
			}

			&:checked~.checkmark:after {
				background: colors-get(gray, 300);
			}
		}
	}

	.checkmark {
		position: absolute;
		left: 0;
		top: 50%;
		transform: translate(0, -50%);
		height: rem(14);
		width: rem(14);
		background-color: colors-get(base, white);
		border-radius: 50%;
		border: 1px solid colors-get(gray, 300);
		cursor: pointer;

		&.top {
			top: 0;
			transform: translate(0, 25%);
		}

		&:after {
			content: "";
			position: absolute;
			display: none;
			left: 50%;
			top: 50%;
			transform: translate(-50%, -50%);
			width: rem(6);
			height: rem(6);
			background: colors-get(primary, 600);
			border-radius: 50%;
		}
	}

	.helper-text {
		margin-top: 0;
	}
}
</style>
